import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FileTrigger, Label, TextField } from "react-aria-components";
import { Loader2, Upload, X } from "lucide-react";
import { PM_MEDIA_EXTENSIONS, PM_MEDIA_MAX_BYTES, pmMediaTypeFor, type PmMicroScenarioView } from "@arc/contracts";
import { Dialog, DialogDescription, DialogFooter, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import type { AuthSession } from "../../auth/keycloak.js";
import { useCreatePmTask, type NewTaskOutcome } from "./usePmBoard.js";
import { useMountedRef, usePmNotices } from "./PmNotices.js";
import { PmTagPicker } from "./pm-tags.js";

const WHOLE_MS = "whole";

/**
 * "Neuer Task" (ADR-055 phase 5, mockup "3"): title (required), description, the step (pre-set to the step it was
 * opened from, or "Ganzes MS"), tags, files, and the meeting flag. The task lands in the start column of the current phase
 * (the server decides both). Files are checked here against the SAME allow-list and limit the server enforces, so a
 * file the upload would refuse is named before anything is sent; the server still checks the content.
 *
 * If the task is created but a file or the flag then fails, the dialog stays open on a result that says exactly that
 * — the task exists, nothing is lost, and re-submitting would only create a duplicate.
 */
export function PmNewTaskDialog({
  session,
  data,
  initialStepId,
  onClose,
}: {
  session: AuthSession;
  data: PmMicroScenarioView;
  initialStepId: number | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const create = useCreatePmTask(session, data.ms.id);
  const mounted = useMountedRef();
  const notices = usePmNotices();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [step, setStep] = useState<string>(initialStepId === null ? WHOLE_MS : String(initialStepId));
  const [tagIds, setTagIds] = useState<number[]>([]);
  const [flagged, setFlagged] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [outcome, setOutcome] = useState<NewTaskOutcome | null>(null);

  const problems = files.flatMap((f) =>
    pmMediaTypeFor(f.name) === null
      ? [t("pmBoard.newTaskDialog.fileType", { name: f.name })]
      : f.size > PM_MEDIA_MAX_BYTES
        ? [t("pmBoard.newTaskDialog.fileTooLarge", { name: f.name })]
        : [],
  );
  const canSubmit = title.trim().length > 0 && problems.length === 0 && !create.isPending && outcome === null;
  const submit = () =>
    create.mutate(
      {
        title: title.trim(),
        description,
        stepId: step === WHOLE_MS ? null : Number(step),
        tagIds,
        flagged,
        files,
        isOpen: () => mounted.current,
        onOrphaned: ({ fileNames, flagFailed }, task) => {
          fileNames.forEach((name) => notices.report(t("pmBoard.notices.uploadOrphaned", { name, task })));
          if (flagFailed) notices.report(t("pmBoard.notices.flagOrphaned", { task }));
        },
      },
      {
        onSuccess: (o) => {
          if (o.failedFiles.length === 0 && !o.flagFailed) onClose();
          else setOutcome(o);
        },
      },
    );

  return (
    // While the create + uploads run, nothing closes the dialog — not the backdrop, not Escape (diff panel): closing it
    // would unmount the one observer that reports a failed upload, and the user would believe the files are attached.
    <Dialog
      isOpen
      size="lg"
      isDismissable={!create.isPending}
      isKeyboardDismissDisabled={create.isPending}
      onOpenChange={(open) => !open && !create.isPending && onClose()}
    >
      <div className="flex items-start gap-3">
        <div className="flex flex-1 flex-col gap-0.5">
          <DialogTitle>{outcome ? t("pmBoard.newTaskDialog.createdTitle") : t("pmBoard.newTaskDialog.title")}</DialogTitle>
          <DialogDescription>{t("pmBoard.newTaskDialog.description", { ms: `${data.ms.code} – ${data.ms.title}` })}</DialogDescription>
        </div>
        <Button variant="ghost" size="icon" aria-label={t("pmBoard.newTaskDialog.close")} onPress={onClose} isDisabled={create.isPending}>
          <X aria-hidden="true" />
        </Button>
      </div>

      {outcome ? (
        <div role="alert" className="flex flex-col gap-2 text-sm">
          {outcome.failedFiles.length > 0 ? <p className="text-destructive m-0">{t("pmBoard.newTaskDialog.partialFiles", { files: outcome.failedFiles.map((f) => f.name).join(", ") })}</p> : null}
          {outcome.flagFailed ? <p className="text-destructive m-0">{t("pmBoard.newTaskDialog.partialFlag")}</p> : null}
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <TextField value={title} onChange={setTitle} isRequired className="flex flex-col gap-1.5">
            <Label className="text-sm font-medium">
              {t("pmBoard.newTaskDialog.titleLabel")} <span className="text-muted-foreground font-normal">{t("pmBoard.newTaskDialog.required")}</span>
            </Label>
            <Input maxLength={500} autoFocus />
          </TextField>

          <TextField value={description} onChange={setDescription} className="flex flex-col gap-1.5">
            <Label className="text-sm font-medium">{t("pmBoard.newTaskDialog.descriptionLabel")}</Label>
            <Textarea rows={4} maxLength={20_000} />
          </TextField>

          <div className="flex flex-col gap-1.5">
            <Select selectedKey={step} onSelectionChange={(k) => k !== null && setStep(String(k))} className="flex flex-col gap-1.5">
              <Label className="text-sm font-medium">{t("pmBoard.newTaskDialog.stepLabel")}</Label>
              <SelectTrigger />
              <SelectContent>
                <SelectItem id={WHOLE_MS}>{t("pmBoard.newTaskDialog.wholeMs")}</SelectItem>
                {data.steps.map((s) => (
                  <SelectItem key={s.id} id={String(s.id)}>
                    {`${s.number} · ${s.title}`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {initialStepId !== null ? <p className="text-muted-foreground m-0 text-xs">{t("pmBoard.newTaskDialog.stepPrefilled")}</p> : null}
          </div>

          <PmTagPicker tags={data.tags} selected={tagIds} isDisabled={create.isPending} onCommit={setTagIds} />

          <fieldset className="m-0 flex flex-col gap-2 border-0 p-0">
            <legend className="mb-2 p-0 text-sm font-medium">{t("pmBoard.newTaskDialog.media")}</legend>
            <div className="border-border flex flex-col items-center gap-1.5 rounded-lg border-2 border-dashed p-3.5">
              <FileTrigger
                allowsMultiple
                acceptedFileTypes={[...PM_MEDIA_EXTENSIONS]}
                onSelect={(list) => {
                  if (list) setFiles((prev) => [...prev, ...Array.from(list)]);
                }}
              >
                <Button variant="outline" size="sm">
                  <Upload aria-hidden="true" />
                  {t("pmBoard.newTaskDialog.chooseFiles")}
                </Button>
              </FileTrigger>
              <span className="text-muted-foreground text-xs">{t("pmBoard.newTaskDialog.mediaHint")}</span>
            </div>
            {files.length > 0 ? (
              <ul className="m-0 flex list-none flex-col gap-1.5 p-0">
                {files.map((f, i) => (
                  <li key={`${f.name}-${i}`} className="bg-muted flex items-center gap-2.5 rounded-lg py-1.5 pr-1.5 pl-3">
                    <span className="flex min-w-0 flex-1 flex-col">
                      <span className="truncate text-[0.8rem] font-medium">{f.name}</span>
                      <span className="text-muted-foreground text-xs tabular-nums">{formatSize(f.size)}</span>
                    </span>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      aria-label={t("pmBoard.newTaskDialog.removeFile", { name: f.name })}
                      isDisabled={create.isPending}
                      onPress={() => setFiles((prev) => prev.filter((_, j) => j !== i))}
                    >
                      <X aria-hidden="true" />
                    </Button>
                  </li>
                ))}
              </ul>
            ) : null}
            {problems.map((p) => (
              <p key={p} role="alert" className="text-destructive m-0 text-sm">
                {p}
              </p>
            ))}
          </fieldset>

          <div className="bg-muted flex flex-col gap-0.5 rounded-lg px-3 py-2.5">
            <Switch isSelected={flagged} onChange={setFlagged}>
              {t("pmBoard.newTaskDialog.meeting")}
            </Switch>
            <span className="text-muted-foreground pl-11 text-xs">{t("pmBoard.newTaskDialog.meetingHint")}</span>
          </div>

          {create.isError ? (
            <p role="alert" className="text-destructive m-0 text-sm">
              {t("pmBoard.newTaskDialog.failed")}
            </p>
          ) : null}
        </div>
      )}

      <DialogFooter>
        {outcome ? (
          <Button variant="default" onPress={onClose}>
            {t("pmBoard.newTaskDialog.close")}
          </Button>
        ) : (
          <>
            <Button variant="ghost" onPress={onClose} isDisabled={create.isPending}>
              {t("pmBoard.newTaskDialog.cancel")}
            </Button>
            <Button variant="default" onPress={submit} isDisabled={!canSubmit}>
              {create.isPending ? (
                <>
                  <Loader2 aria-hidden="true" className="size-4 animate-spin" />
                  {t("pmBoard.newTaskDialog.creating")}
                </>
              ) : (
                t("pmBoard.newTaskDialog.create")
              )}
            </Button>
          </>
        )}
      </DialogFooter>
    </Dialog>
  );
}

function formatSize(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}
